import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
export function SolutionSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const steps = ['entender una oportunidad actual,', 'convertirla en una oferta concreta,', 'crear una base de servicio fácil de presentar,', 'y empezar a moverte con más claridad hacia tus primeros ingresos.'];
  return <section className="relative" data-id="element-281">
      {/* Top half: Navy background */}
      <div className="bg-navy text-white py-20 md:py-32" data-id="element-282">
        <div className="container mx-auto px-4 max-w-4xl text-center" data-id="element-283">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} data-id="element-284">
            <motion.p variants={itemVariants} className="text-gold text-lg md:text-xl font-medium mb-4" data-id="element-285">
              Porque existe una forma mucho más práctica de comenzar:
            </motion.p>
            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12 leading-tight" data-id="element-286">
              usar estrategias de Inteligencia Artificial para crear servicios
              visuales simples, útiles y vendibles, directamente desde tu
              celular.
            </motion.h2>

            <motion.div variants={itemVariants} className="inline-block bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 backdrop-blur-sm w-full" data-id="element-287">
              <h3 className="text-2xl md:text-3xl font-serif text-gold mb-6" data-id="element-288">
                Presentamos: IA Express 2026
              </h3>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed text-left" data-id="element-289">
                IA Express 2026 es una guía práctica creada para ayudarte a dar
                tus primeros pasos en un mini-negocio visual usando estrategias
                de IA de forma simple, rápida y accionable.
              </p>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed text-left" data-id="element-290">
                No se trata de convertirte en experta. No se trata de llenarte
                de teoría. No se trata de hacerte sentir más confundida que
                antes. Se trata de mostrarte una ruta clara para que puedas:
              </p>

              <div className="space-y-4 text-left mb-8" data-id="element-291">
                {steps.map((step, index) => <motion.div key={index} variants={itemVariants} className="flex items-start gap-3" data-id="element-292">
                    <CheckCircle2Icon className="w-6 h-6 text-gold shrink-0 mt-0.5" data-id="element-293" />
                    <span className="text-lg text-white" data-id="element-294">{step}</span>
                  </motion.div>)}
              </div>

              <p className="text-lg text-slate-300 leading-relaxed text-left italic border-l-2 border-gold/50 pl-4" data-id="element-295">
                Todo con un enfoque práctico. Todo explicado de forma simple.
                Todo pensado para una mujer real, ocupada, con poco tiempo y con
                ganas de empezar sin complicarse.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom half: White background */}
      <div className="bg-white py-20 md:py-32" data-id="element-296">
        <div className="container mx-auto px-4 max-w-4xl" data-id="element-297">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} className="mb-20" data-id="element-298">
            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-navy mb-8 text-center md:text-left" data-id="element-299">
              ¿Qué hace diferente a IA Express 2026?
            </motion.h2>

            <div className="space-y-6 text-lg text-slate-700 leading-relaxed" data-id="element-300">
              <motion.p variants={itemVariants} data-id="element-301">
                Hay mucha información sobre Inteligencia Artificial allá afuera.
                Pero la mayoría sirve para una sola cosa: abrumarte.
              </motion.p>
              <motion.p variants={itemVariants} data-id="element-302">
                Mucho contenido. Mucho ruido. Muchas posibilidades. Y poca
                claridad sobre cómo transformar eso en algo útil y monetizable.
              </motion.p>
              <motion.p variants={itemVariants} className="font-medium text-navy" data-id="element-303">
                IA Express 2026 es diferente porque no fue creado para
                impresionarte con términos técnicos. Fue creado para ayudarte a
                actuar.
              </motion.p>
              <motion.p variants={itemVariants} data-id="element-304">
                Aquí no vas a entrar en un laberinto de herramientas, conceptos
                y explicaciones eternas. Aquí vas a encontrar una estructura
                clara para comenzar con una oportunidad real, actual y mucho más
                accesible de lo que imaginas.
              </motion.p>
              <motion.div variants={itemVariants} className="bg-cream p-6 rounded-xl border border-gold/20 mt-8" data-id="element-305">
                <p className="text-navy font-semibold text-center" data-id="element-306">
                  En lugar de perderte intentando entender "todo sobre IA", vas
                  a enfocarte en algo mucho más importante:{' '}
                  <span className="text-gold" data-id="element-307">
                    cómo usarla estratégicamente para crear una oferta visual
                    simple que puedas lanzar desde tu celular.
                  </span>
                </p>
              </motion.div>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} data-id="element-308">
            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-navy mb-8 text-center md:text-left" data-id="element-309">
              Imagina esto…
            </motion.h2>

            <div className="space-y-6 text-lg text-slate-700 leading-relaxed" data-id="element-310">
              <motion.p variants={itemVariants} data-id="element-311">
                Imagínate teniendo una forma clara de crear una oferta visual
                atractiva y útil para negocios o emprendedores que necesitan
                verse mejor, comunicar mejor o proyectarse de manera más
                profesional.
              </motion.p>
              <motion.p variants={itemVariants} data-id="element-312">
                Imagínate dejando de mirar la tecnología desde fuera, como si
                fuera algo complicado o lejano… y empezando a usarla a tu favor,
                de forma práctica.
              </motion.p>
              <motion.p variants={itemVariants} data-id="element-313">
                Imagínate sentir, por fin, que sí puedes construir algo tuyo.
                Algo simple. Algo actual. Algo que no dependa de vender
                productos físicos, invertir grandes cantidades o pasar meses
                estudiando sin resultados.
              </motion.p>

              <motion.div variants={itemVariants} className="pt-8 mt-8 border-t border-slate-200 space-y-4" data-id="element-314">
                <p className="font-medium text-navy" data-id="element-315">
                  Eso es justamente lo que busca IA Express 2026.
                </p>
                <p data-id="element-316">
                  No prometerte fantasías. No venderte humo. Sino ayudarte a
                  pasar de la confusión a la claridad.
                </p>
                <p className="text-xl font-serif font-bold text-gold" data-id="element-317">
                  Esto no es para "algún día". Esto es para empezar ya.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-navy text-white p-8 rounded-2xl mt-8 shadow-xl" data-id="element-318">
                <p className="text-gold font-medium mb-2" data-id="element-319">
                  Porque la verdad es esta:
                </p>
                <p className="mb-4" data-id="element-320">
                  No necesitas tener todo perfecto para comenzar. No necesitas
                  sentirte 100% lista. No necesitas saberlo todo antes de dar el
                  primer paso.
                </p>
                <p className="font-medium" data-id="element-321">
                  Lo que sí necesitas es una guía que te ayude a ordenar tus
                  ideas, entender la oportunidad y transformarla en acción.
                </p>
                <p className="mt-4 text-gold italic" data-id="element-322">
                  Y eso es exactamente lo que IA Express 2026 viene a darte.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}